import React from 'react';
import PropTypes from 'prop-types';

import { StructureOptionType } from '@Pages/RetroSynthesis/StructureOption/constants';
import SvgUseAsButton from '@Pages/RetroSynthesis/ProjectRoute/ProjectRouteTreeView/components/SvgUseAsButton';

// RX-3549 - remove imports once feature switcher is removed
import Auth from '@Services/Auth';
import { FEATURES } from '@Constants/rx-ui/constants';
import { getShoppingCartIcon } from '@Components/Results/structure/availability/ShoppingCartHelper';
import ReactDOMServer from 'react-dom/server';
import ShoppingCartTooltipContent from '@Components/Results/structure/availability/ShoppingCartTooltipContent';
import { isCustomerIconVisible, getCustomerSpecificMapping } from '@Components/Results/structure/structure-utils';
import StructureToolbarCustomerIconTooltip from '@Components/Results/structure/StructureToolbarCustomerIconTooltip';

const customerButton = (x, substanceHeight, icon) => ({
  href: `#icon-${icon}`,
  height: 32,
  width: 32,
  x,
  y: substanceHeight - 32,
});

const shoppingCartNumberLabel = (offsetX, substanceHeight) => ({
  x: 32 + offsetX,
  y: substanceHeight - 12,
});

const shoppingCartNumberIcon = (offsetX, substanceHeight) => ({
  height: '1.3rem',
  width: '1.3rem',
  x: 21 + offsetX,
  y: substanceHeight - 26,
  rx: 10,
  ry: 10,
});

const shoppingCartButton = (offsetX, substanceHeight, icon) => ({
  href: icon,
  height: 26,
  width: 26,
  x: 40 + offsetX,
  y: substanceHeight - 28,
});

const zoomButton = (offsetX, substanceHeight) => ({
  href: '#icon-structure-zoom',
  height: 26,
  width: 26,
  x: 74 + offsetX,
  y: substanceHeight - 28,
});

const detailsButton = (offsetX, substanceHeight) => ({
  href: '#icon-structure-details',
  height: 26,
  width: 26,
  x: 108 + offsetX,
  y: substanceHeight - 28,
});

const synthesisPlannerButton = (offsetX, substanceHeight) => ({
  href: '#icon-ellipsis',
  title: 'View Options',
  height: 26,
  width: 26,
  x: 142 + offsetX,
  y: substanceHeight - 28,
});

function StructureButtons({
  substanceHeight,
  shoppingCartVisible,
  onOptionClick,
  isZoomAvailable,
  supplierNumber,
  viewDetailsEnabled,
  optionsEnabled,
  substanceDetails,
  customerInfo,
}) {
  const showCustomerIcon = isCustomerIconVisible(customerInfo);
  const offsetX = showCustomerIcon && shoppingCartVisible ? 20 : 0;
  const ciX = shoppingCartVisible ? 10 : 40;
  const shoppingCartNumber = Auth.isFeatureEnabled(FEATURES.enableDisplaySupplierNumber) && supplierNumber && (
    <>
      <rect {...shoppingCartNumberIcon(offsetX, substanceHeight)} fill="#ebebeb" />
      <text
        {...shoppingCartNumberLabel(offsetX, substanceHeight)}
        textAnchor="middle"
        fill="black"
        className="rt-project-route-substance-node--shopping-cart-number">
        {supplierNumber}
      </text>
    </>
  );
  const { icon: customerIcon, label } = getCustomerSpecificMapping(customerInfo.name) || {};
  const shoppingCartIcon = getShoppingCartIcon(substanceDetails);
  return (
    <>
      {showCustomerIcon && (
        <SvgUseAsButton
          onClick={(e) =>
            onOptionClick({
              type: StructureOptionType.CUSTOMER_ICON,
              targetRect: e.target.getBoundingClientRect(),
            })
          }
          wrapperClassName="rt-project-route-substance-node--button"
          className={`rt-project-route-substance-node--customer-icon ${customerIcon}`}
          tabIndex="0"
          tooltip={ReactDOMServer.renderToString(<StructureToolbarCustomerIconTooltip customerLabel={label} />)}
          {...customerButton(ciX, substanceHeight, customerIcon)}
        />
      )}
      {shoppingCartVisible && (
        <>
          {shoppingCartNumber}
          <SvgUseAsButton
            onClick={(e) =>
              onOptionClick({
                type: StructureOptionType.SHOPPING_CARD,
                targetRect: e.target.getBoundingClientRect(),
              })
            }
            tabIndex="0"
            tooltip={ReactDOMServer.renderToString(<ShoppingCartTooltipContent details={substanceDetails} />)}
            wrapperClassName="rt-project-route-substance-node--button"
            className="rt-project-route-substance-node--purchase-button"
            {...shoppingCartButton(offsetX, substanceHeight, shoppingCartIcon)}
          />
        </>
      )}
      {isZoomAvailable && (
        <SvgUseAsButton
          onClick={(e) =>
            onOptionClick({
              type: StructureOptionType.ZOOM,
              targetRect: e.target.getBoundingClientRect(),
            })
          }
          wrapperClassName="rt-project-route-substance-node--button"
          className="rt-project-route-substance-node--zoom"
          tabIndex="0"
          tooltip="Zoom in"
          {...zoomButton(offsetX, substanceHeight)}
        />
      )}
      {viewDetailsEnabled && (
        <SvgUseAsButton
          onClick={(e) =>
            onOptionClick({
              type: StructureOptionType.DETAILS,
              targetRect: e.target.getBoundingClientRect(),
            })
          }
          wrapperClassName="rt-project-route-substance-node--button"
          className="rt-project-route-substance-node--zoom"
          tabIndex="0"
          tooltip="View Details"
          {...detailsButton(offsetX, substanceHeight)}
        />
      )}
      {optionsEnabled && (
        <SvgUseAsButton
          onClick={(e) =>
            onOptionClick({
              type: StructureOptionType.OPTIONS,
              targetRect: e.target.getBoundingClientRect(),
            })
          }
          wrapperClassName="rt-project-route-substance-node--button"
          className="rt-project-route-substance-node--synthesis-planner"
          tabIndex="0"
          tooltip="View Options"
          {...synthesisPlannerButton(offsetX, substanceHeight)}
        />
      )}
    </>
  );
}

StructureButtons.defaultProps = {
  onOptionClick: () => {},
  isZoomAvailable: true,
  customerInfo: {},
};

StructureButtons.propTypes = {
  substanceHeight: PropTypes.number,
  supplierNumber: PropTypes.number,
  shoppingCartVisible: PropTypes.bool,
  onOptionClick: PropTypes.func,
  isZoomAvailable: PropTypes.bool,
  optionsEnabled: PropTypes.bool,
  viewDetailsEnabled: PropTypes.bool,
  substanceDetails: PropTypes.object,
  customerInfo: PropTypes.object,
};

export default StructureButtons;
